import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import ListingsModal from 'views/V2/Integration/components/ListingsModal';
import NoResultFound from 'views/V2/Integration/components/NoResultFound';
import SearchBrandAndListings from 'views/V2/Integration/components/SearchBrandAndListings';
import { useListings } from 'views/V2/Integration/hooks/useListings';
import { useManageListingsSelection } from 'views/V2/Integration/hooks/useManageListingsSelection';

import { selectListingsCounts } from 'redux/selectors/integrations';

import styles from './manageListings.module.css';

import Filter from './components/Filter';
import ListingTable from './components/ListingTable';

const ManageListings = ({ open, onClose, brandUuid }) => {
    const [filters, setFilters] = useState({ enabled: true, disabled: true });
    const { totalListingsCount, totalDisabledListings } = useSelector(selectListingsCounts);

    // API filter: true = enabled only, false = disabled only, null = all. Always returns boolean | null for Sonar S3800.
    const enabledFilter = useMemo(() => {
        if (filters.enabled && !filters.disabled) return true; // enabled listings from API
        if (!filters.enabled && filters.disabled) return false; // disabled listings from API
        // both on => show all from API; both off => API gets all, we filter on frontend to show none
        return null;
    }, [filters]);

    const isFrontendFilterNone = !filters.enabled && !filters.disabled;

    const {
        listingRef,
        handleScroll,
        isLoading,
        isFetching,
        rows,
        searchTerm,
        setSearchTerm,
        searchTags,
        setSearchTags,
        onSubmit,
        onRemoveSearchTag,
        refreshListings,
        onSaveChanges,
        totalCount,
    } = useListings('manage', brandUuid, enabledFilter);

    const {
        selection,
        hasChanges,
        toggle,
        selectAll,
        clearSelection,
        saveChanges,
        resetToBaseline,
        loading,
        effectiveRows,
        netSelectionChange,
    } = useManageListingsSelection({
        open,
        rows,
        onSaveChanges,
        refreshListings,
    });

    const displayedRows = useMemo(() => {
        if (isFrontendFilterNone) return [];
        return effectiveRows;
    }, [isFrontendFilterNone, effectiveRows]);

    const handleClose = () => {
        // Clear search state when closing the modal
        setSearchTags([]);
        setSearchTerm('');
        onClose();
    };

    const handleCancel = () => {
        resetToBaseline();
        handleClose();
    };

    const handleSaveClick = () => {
        saveChanges(() => {
            handleClose();
        });
    };

    const onToggleFilter = useCallback((key) => {
        setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const initialTotalEnabled = totalListingsCount - totalDisabledListings;
    const allListingsDeselected =
        totalListingsCount > 0 && initialTotalEnabled + netSelectionChange <= 0;

    const isSearchActive = searchTags.length > 0;

    const filteredTotalCount = useMemo(() => {
        if (isFrontendFilterNone) return 0;
        if (isSearchActive) return totalCount ?? 0;
        if (filters.enabled && !filters.disabled) return initialTotalEnabled;
        if (!filters.enabled && filters.disabled) return totalDisabledListings;
        return totalListingsCount;
    }, [
        filters,
        isFrontendFilterNone,
        isSearchActive,
        totalCount,
        initialTotalEnabled,
        totalDisabledListings,
        totalListingsCount,
    ]);

    // When the modal is closed, reset filters back to the default state
    useEffect(() => {
        if (!open) {
            setFilters({ enabled: true, disabled: true });
        }
    }, [open]);

    return (
        <ListingsModal
            open={open}
            onClose={handleClose}
            className={styles.manageListingsModalContainer}
        >
            <ListingsModal.Header
                title="Manage Listings"
                subTitle="Checked listings will automatically create a user and verification link for new reservations. Unchecked listings will not."
                onClose={handleClose}
            >
                <SearchBrandAndListings
                    value={searchTerm}
                    setValue={setSearchTerm}
                    onSubmit={onSubmit}
                    tags={searchTags}
                    setTags={setSearchTags}
                    removeSearchTag={onRemoveSearchTag}
                    filterComponent={<Filter filters={filters} onToggleFilter={onToggleFilter} />}
                />
            </ListingsModal.Header>
            <ListingsModal.Content>
                {displayedRows.length > 0 ? (
                    <Box className={styles.contentWrapper}>
                        <ListingTable
                            rows={displayedRows}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            scrollContainerRef={listingRef}
                            onScroll={handleScroll}
                            totalCount={filteredTotalCount}
                            filteredListings={selection}
                            handleFilterListings={toggle}
                            onSelectAll={selectAll}
                            onClearSelection={clearSelection}
                        />
                    </Box>
                ) : (
                    <NoResultFound />
                )}
            </ListingsModal.Content>
            {displayedRows.length > 0 && (
                <ListingsModal.Footer
                    infoText={
                        allListingsDeselected
                            ? 'You are trying to disable all listings. To pause the integration, please deactivate your integration.'
                            : ''
                    }
                    infoTextClassName={
                        allListingsDeselected ? styles.deselectListingErrorText : undefined
                    }
                    primaryBtnProps={{
                        title: 'Save Changes',
                        onClick: handleSaveClick,
                        disabled: allListingsDeselected || !hasChanges || loading,
                    }}
                    secondaryBtnProps={{
                        title: 'Cancel',
                        onClick: handleCancel,
                    }}
                    showLoader={loading}
                />
            )}
        </ListingsModal>
    );
};

ManageListings.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    brandUuid: PropTypes.string.isRequired,
};

export default ManageListings;
